import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useFieldError, useT } from '@kmgestao/i18n';
import { Button, Input, Label, PageSectionCard, toast } from '@kmgestao/ui';

import {
  resetPasswordConfirmSchema,
  type ResetPasswordConfirmInput,
} from '../../../features/auth/index.js';
import { supabase } from '../../../lib/supabase.js';

export interface PasswordSetFormProps {
  /** Email do owner (apenas pra exibir contexto). */
  email: string;
  /** Callback chamado APÓS senha salva com sucesso E `password_set` em
   * user_metadata atualizado. O caller deve então prosseguir com a
   * cascata de aceite (membership, lead, refresh JWT, redirect). */
  onPasswordSaved: () => void | Promise<void>;
  /** Quando `true`, desabilita o botão de submit (caller controlando
   * fase posterior do fluxo, ex: aceite em andamento). */
  busy?: boolean;
}

/**
 * Formulário de "defina sua senha" exibido na tela `/welcome` quando o
 * owner está ativando a conta pela primeira vez (sem `password_set` em
 * `user.user_metadata`).
 *
 * Reusa `resetPasswordConfirmSchema` do `@kmgestao/schemas` — mesma regra
 * de força de senha (12 chars, maiúscula+minúscula+dígito+símbolo) +
 * confirmação. Strings reaproveitam keys de validation já em pt-BR.
 *
 * **Por que aqui e não em rota separada `/welcome/set-password`**:
 * minimizar redirects (cada navigate adicional aumenta chance de quebrar
 * por refresh/back-button). Owner já está autenticado via magic link
 * quando vê esse form — `auth.updateUser({ password })` funciona sem
 * exigir reauth.
 *
 * **Por que setar `data: { password_set: true }` no metadata**: precisamos
 * detectar "owner já passou pela 1ª ativação" em logins futuros pra NÃO
 * mostrar o form de novo. `password_set` é a flag canônica — qualquer
 * código futuro deve respeitá-la antes de pedir senha de novo.
 */
export function PasswordSetForm({ email, onPasswordSaved, busy = false }: PasswordSetFormProps) {
  const { t } = useT('clinics');
  const { t: tCommon } = useT('common');
  const tError = useFieldError();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordConfirmInput>({
    resolver: zodResolver(resetPasswordConfirmSchema),
    defaultValues: { password: '', passwordConfirmation: '' },
  });

  const passwordError = tError(errors.password);
  const passwordConfirmationError = tError(errors.passwordConfirmation);

  const onSubmit = handleSubmit(async (values) => {
    // Salva senha + flag `password_set: true` no user_metadata. A flag
    // é nosso marcador interno de "owner já passou pela 1ª ativação"
    // — em refresh ou revisita ao link, WelcomePage detecta a flag e
    // pula o form (vai direto pro destino). É independente do
    // `email_confirmed_at` (que o Supabase mantém por outros motivos).
    const { error } = await supabase.auth.updateUser({
      password: values.password,
      data: { password_set: true },
    });
    if (error) {
      toast.error(t('welcome.setPassword.errors.saveFailed'), {
        description: error.message,
      });
      return;
    }
    toast.success(t('welcome.setPassword.successTitle'));
    await onPasswordSaved();
  });

  const submitDisabled = isSubmitting || busy;

  return (
    <PageSectionCard
      title={t('welcome.setPassword.title')}
      description={t('welcome.setPassword.description', { email })}
      contentClassName="space-y-4"
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor="welcome-password">{t('welcome.setPassword.fields.password')}</Label>
          <Input
            id="welcome-password"
            type="password"
            autoComplete="new-password"
            inputMode="text"
            hasError={!!errors.password}
            disabled={submitDisabled}
            {...register('password')}
          />
          <p className="text-sm text-muted-foreground">
            {passwordError ?? t('welcome.setPassword.fields.passwordHint')}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="welcome-password-confirm">
            {t('welcome.setPassword.fields.passwordConfirmation')}
          </Label>
          <Input
            id="welcome-password-confirm"
            type="password"
            autoComplete="new-password"
            inputMode="text"
            hasError={!!errors.passwordConfirmation}
            disabled={submitDisabled}
            {...register('passwordConfirmation')}
          />
          {passwordConfirmationError ? (
            <p className="text-sm text-destructive" role="alert">
              {passwordConfirmationError}
            </p>
          ) : null}
        </div>

        <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={submitDisabled}>
          {submitDisabled ? tCommon('status.saving') : t('welcome.setPassword.actions.save')}
        </Button>
      </form>
    </PageSectionCard>
  );
}
